import $ from 'jquery';
import { AppStateProvider, simpleEffect } from '../shared/app_state/AppStateProvider';
import { LS_STATE_TAG, URL_STATE_TAG } from './MainService';
import { isBool } from '../shared/utilities/validation';
import { urlParamVal, urlParamValEq } from '../shared/url_and_history/url_and_history';

export const k_CENTER_COLLAPSED = 'center_collapsed' as const;
export const k_SHOW_OVERVIEW = 'show_overview_pane' as const;
export const k_SHOW_MAP = 'show_map_pane' as const;
export const k_SHOW_LICENSE_BANNER = 'show_license_banner' as const;
export const k_SHOW_EDITOR_BANNER = 'show_editor_banner' as const;
export const k_SHOW_MAP_POSITION_BANNER = 'show_map_position_banner' as const;
export const k_SHOW_MAP_BOUNDARIES_BANNER = 'show_map_boundaries_banner' as const;

const k_WINDOW_SIZE = 'window_size' as const;

function updatePanesCSS(appState, disableTransition) {
    const showMap = appState[k_SHOW_MAP];
    const showOverview = appState[k_SHOW_OVERVIEW];
    const centerCollapsed = appState[k_CENTER_COLLAPSED];
    const windowWidth = appState[k_WINDOW_SIZE].width;
    const centerWidth = centerCollapsed ? 60 : 265;
    const singlePaneMode = showMap !== showOverview;
    const direction = showMap ? -1 : 1;
    const shiftedSide = showMap ? 'right' : 'left';
    const otherSide = showMap ? 'left' : 'right';
    const translateAmount = singlePaneMode
        ? Math.round(direction * 0.5 * windowWidth + -direction * 0.5 * centerWidth)
        : 0;
    const sideShiftAmount = Math.round(-0.5 * windowWidth + 0.5 * centerWidth);
    const panesContainerWidth = singlePaneMode
        ? Math.round(2 * windowWidth - centerWidth)
        : windowWidth;

    $('#panes-container').css({
        transition: disableTransition ? 'none' : '.65s ease transform',
        width: `${panesContainerWidth}px`,
        transform: `translateX(${translateAmount}px)`,
        [shiftedSide]: singlePaneMode ? `${sideShiftAmount}px` : 'initial',
        [otherSide]: 'initial',
    });

    $('#center-pane').css({
        width: `${centerWidth}px`,
    });
}

const paneKeys = [
    k_CENTER_COLLAPSED,
    k_SHOW_OVERVIEW,
    k_SHOW_MAP,
    k_SHOW_LICENSE_BANNER,
    k_SHOW_EDITOR_BANNER,
    k_SHOW_MAP_POSITION_BANNER,
    k_SHOW_MAP_BOUNDARIES_BANNER,
] as const;

export class PanesService {
    state: AppStateProvider;

    constructor($rootScope, mainService, forcedCollapsedMode = false) {
        // make sure they both don't initialise as false
        const initialShowOverview = mainService.initialStateValue(k_SHOW_OVERVIEW);
        const initialShowBanner = false;
        const initialShowMap = !initialShowOverview
            ? urlParamVal(true)
            : mainService.initialStateValue(k_SHOW_MAP);

        this.state = new AppStateProvider({
            keys: {
                [k_CENTER_COLLAPSED]: {
                    initialValue: forcedCollapsedMode
                        ? true
                        : mainService.initialStateValue(k_CENTER_COLLAPSED),
                    defaultValue: forcedCollapsedMode,
                    validator: isBool,
                    tags: [LS_STATE_TAG],
                },
                [k_SHOW_OVERVIEW]: {
                    initialValue: initialShowOverview,
                    defaultValue: urlParamVal(false),
                    validator: isBool,
                    eqCheck: urlParamValEq,
                    tags: [URL_STATE_TAG],
                },
                [k_SHOW_MAP]: {
                    initialValue: initialShowMap,
                    defaultValue: urlParamVal(true),
                    validator: isBool,
                    eqCheck: urlParamValEq,
                    tags: [URL_STATE_TAG],
                },
                [k_SHOW_LICENSE_BANNER]: {
                    initialValue: initialShowBanner,
                    defaultValue: urlParamVal(true),
                    validator: isBool,
                    eqCheck: urlParamValEq,
                    //Leave tags empty to remove from address url
                    tags: [],
                },
                [k_SHOW_EDITOR_BANNER]: {
                    initialValue: false,
                    defaultValue: urlParamVal(false),
                    validator: isBool,
                    eqCheck: urlParamValEq,
                    tags: [],
                },
                [k_SHOW_MAP_POSITION_BANNER]: {
                    initialValue: false,
                    defaultValue: urlParamVal(false),
                    validator: isBool,
                    eqCheck: urlParamValEq,
                    tags: [],
                },
                [k_SHOW_MAP_BOUNDARIES_BANNER]: {
                    initialValue: false,
                    defaultValue: urlParamVal(false),
                    validator: isBool,
                    eqCheck: urlParamValEq,
                    tags: [],
                },
            },
            effects: {
                toggleCenterCollapsed: simpleEffect((state, bool) => ({
                    [k_CENTER_COLLAPSED]: !state[k_CENTER_COLLAPSED],
                })),
                setShowLicenseBanner: simpleEffect((_changes, bool) => ({
                    [k_SHOW_LICENSE_BANNER]: bool,
                })),
                setShowEditorBanner: simpleEffect((_changes, bool) => ({
                    [k_SHOW_EDITOR_BANNER]: bool,
                })),
                setShowMapPositionBanner: simpleEffect((_changes, bool) => ({
                    [k_SHOW_MAP_POSITION_BANNER]: bool,
                })),
                setShowBoundariesBanner: simpleEffect((_changes, bool) => ({
                    [k_SHOW_MAP_BOUNDARIES_BANNER]: bool,
                })),
                setVisiblePanes: simpleEffect((_, changes) =>
                    Object.assign(
                        {},
                        changes[k_SHOW_MAP] !== undefined
                            ? { [k_SHOW_MAP]: changes[k_SHOW_MAP] }
                            : {},
                        changes[k_SHOW_OVERVIEW] !== undefined
                            ? { [k_SHOW_OVERVIEW]: changes[k_SHOW_OVERVIEW] }
                            : {},
                    ),
                ),
            },
            parent: mainService.state,
        });

        this.state.onChange(
            (state) =>
                $rootScope.$evalAsync(() => {
                    updatePanesCSS(state, false);
                }),
            {
                keys: [k_SHOW_OVERVIEW, k_SHOW_MAP],
            },
        );

        this.state.onChange(
            (state) =>
                $rootScope.$evalAsync(() => {
                    updatePanesCSS(state, true);
                }),
            {
                keys: [k_CENTER_COLLAPSED, k_WINDOW_SIZE],
            },
        );
    }

    showMap() {
        this.state.effects.setVisiblePanes({
            [k_SHOW_MAP]: true,
            [k_SHOW_OVERVIEW]: false,
        });
    }

    showOverview() {
        this.state.effects.setVisiblePanes({
            [k_SHOW_MAP]: false,
            [k_SHOW_OVERVIEW]: true,
        });
    }

    showBoth() {
        return this.state.effects.setVisiblePanes({
            [k_SHOW_MAP]: true,
            [k_SHOW_OVERVIEW]: true,
        });
    }

    initialisePanesCSS() {
        updatePanesCSS(this.state.getState(), true);
    }

    public setVisiblePanes(effectArgs: { [key: string]: boolean }): void {
        this.state.effects.setVisiblePanes(effectArgs);
    }

    public getPanesState(): Record<(typeof paneKeys)[number], boolean> {
        return {
            [k_CENTER_COLLAPSED]: this.state.getState()[k_CENTER_COLLAPSED],
            [k_SHOW_MAP]: this.state.getState()[k_SHOW_MAP],
            [k_SHOW_OVERVIEW]: this.state.getState()[k_SHOW_MAP],
            [k_SHOW_LICENSE_BANNER]: this.state.getState()[k_SHOW_LICENSE_BANNER],
            [k_SHOW_EDITOR_BANNER]: this.state.getState()[k_SHOW_EDITOR_BANNER],
            [k_SHOW_MAP_POSITION_BANNER]: this.state.getState()[k_SHOW_MAP_POSITION_BANNER],
            [k_SHOW_MAP_BOUNDARIES_BANNER]: this.state.getState()[k_SHOW_MAP_BOUNDARIES_BANNER],
        };
    }
}
